import { artifactRevision, experimentCanonical, type ExperimentReceipt } from './experiments'

export const EXPERIMENT_REVIEW_KINDS = [
  'finite-coverage',
  'exact-counterexample',
  'numerical-result',
  'universal-argument',
] as const
export interface ExperimentReviewOptions {
  kind: (typeof EXPERIMENT_REVIEW_KINDS)[number]
  scope: string
  publicArtifactIds: string[]
  candidateOutput: boolean
}

/** Evaluator material and every artifact depending on it remain private even
 * if a proposed review mistakenly requests them. Candidate stdout has its own
 * explicit disclosure switch; evaluator stdout/stderr never enter this packet. */
export function publicExperimentArtifacts(receipt: ExperimentReceipt) {
  const denied = new Set([receipt.spec.evaluator.artifactId, receipt.spec.evaluatorDataId])
  for (const artifact of receipt.artifacts)
    if (artifact.kind === 'evaluator') denied.add(artifact.id)
  let changed = true
  while (changed) {
    changed = false
    for (const artifact of receipt.artifacts)
      if (!denied.has(artifact.id) && artifact.dependsOn.some((d) => denied.has(d.id))) {
        denied.add(artifact.id)
        changed = true
      }
  }
  return receipt.artifacts.filter(
    (a) => !denied.has(a.id) && receipt.spec.inputs.some((r) => r.id === a.id),
  )
}

export function defaultExperimentReview(receipt: ExperimentReceipt): ExperimentReviewOptions {
  const available = new Set(publicExperimentArtifacts(receipt).map((a) => a.id))
  return {
    kind: 'finite-coverage',
    scope: `Only the retained cases and seeds (${receipt.spec.seeds.join(', ')}) under the frozen experiment assumptions.`,
    publicArtifactIds: [receipt.spec.candidate.artifactId, receipt.spec.dataId].filter((id) =>
      available.has(id),
    ),
    candidateOutput:
      available.has(receipt.spec.candidate.artifactId) && available.has(receipt.spec.dataId),
  }
}

export function parseExperimentReview(value: unknown): ExperimentReviewOptions | null {
  if (!value || typeof value !== 'object') return null
  const v = value as ExperimentReviewOptions
  if (
    !EXPERIMENT_REVIEW_KINDS.includes(v.kind) ||
    typeof v.scope !== 'string' ||
    !v.scope.trim() ||
    v.scope.length > 4000 ||
    typeof v.candidateOutput !== 'boolean' ||
    !Array.isArray(v.publicArtifactIds) ||
    v.publicArtifactIds.length > 256 ||
    new Set(v.publicArtifactIds).size !== v.publicArtifactIds.length ||
    !v.publicArtifactIds.every((id) => typeof id === 'string' && id.length > 0 && id.length <= 100)
  )
    return null
  return {
    kind: v.kind,
    scope: v.scope,
    publicArtifactIds: [...v.publicArtifactIds],
    candidateOutput: v.candidateOutput,
  }
}

export function experimentReviewPackage(
  receipt: ExperimentReceipt,
  options = defaultExperimentReview(receipt),
) {
  const review = parseExperimentReview(options)
  if (!review) throw new Error('The review scope or disclosure selection is invalid.')
  const publicArtifacts = publicExperimentArtifacts(receipt)
  if (review.publicArtifactIds.some((id) => !publicArtifacts.some((a) => a.id === id)))
    throw new Error(
      'The review requests private evaluator material or an artifact outside the executed inputs.',
    )
  if (
    review.candidateOutput &&
    ![receipt.spec.candidate.artifactId, receipt.spec.dataId].every((id) =>
      publicArtifacts.some((a) => a.id === id),
    )
  )
    throw new Error(
      'Candidate output depends on private evaluator material and cannot enter this review.',
    )
  const artifacts = publicArtifacts.filter((a) => review.publicArtifactIds.includes(a.id))
  const checker = receipt.artifacts.find((a) => a.id === receipt.spec.evaluator.artifactId)
  const packet = {
    version: 1,
    receiptId: receipt.id,
    inputDigest: receipt.inputDigest,
    declaredScope: {
      kind: review.kind,
      statement: review.scope,
      authority: 'review-requester-declaration',
    },
    publicArtifacts: artifacts.map((a) => ({ ...a, revision: artifactRevision(a) })),
    exactCandidateResults: review.candidateOutput
      ? receipt.runs.map((run) => ({
          seed: run.seed,
          stdout: run.candidate.stdout,
          exitCode: run.candidate.exitCode,
          signal: run.candidate.signal,
          limited: run.candidate.limited,
        }))
      : [],
    checker: {
      artifactId: receipt.spec.evaluator.artifactId,
      revision: checker ? artifactRevision(checker) : null,
      authority: receipt.spec.evaluator.authority,
      runnerDigest: receipt.isolation.runnerDigest,
      runtime: receipt.isolation.runtime,
      isolation: receipt.isolation.kind,
    },
    consumedRevisions: receipt.spec.inputs,
    omissions: {
      evaluatorCode: 'private',
      evaluatorData: 'private',
      evaluatorLogs: 'private',
      candidateStderr: 'withheld',
      candidateStdout: review.candidateOutput ? 'included-exactly' : 'withheld',
      artifacts: publicArtifacts
        .filter((a) => !review.publicArtifactIds.includes(a.id))
        .map((a) => a.id),
    },
    limitation:
      'The scope is a declaration, not an earned certification. Finite coverage is not a universal result. An exact counterexample needs its assumptions and certificate checked; numerical output is not an exact proof. Executing an argument does not establish a universal theorem. Checker identity identifies retained bytes, not an independent authority.',
  }
  if (experimentCanonical(packet).length > 200_000)
    throw new Error(
      'The public review package exceeds 200,000 characters. Select fewer public artifacts or withhold candidate output; nothing was silently clipped.',
    )
  return packet
}
